import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { StatusBadge } from "components/Productlocationstatus";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { ViewIconButton, EditIconButton, DeleteIconButton, UnlinkIconButton, CancelIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import {
  Product,
  LinkedInstaller,
  Address,
  Office,
} from "types";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type Dimensions = {
  length: number;
  height: number;
  width: number;
  weight: number;
};

type Variant = {
  variant_name: string;
  sku: string;
  attributes: string;
  purchase_price: number;
  retail_price: number;
  package_dimensions: {
    length: number;
    height: number;
    width: number;
    weight: number;
    weight_type: string;
  };
  inventory: [
    {
      location_id: string;
      location_name: string;
      on_hand: number;
      available: number;
      reserved: number;
    }
  ];
};

type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type Image = {
  url: string;
};

type OfficeProduct = {
  product_id: string;
  office: Office & { id?: string };
  linked_installer_details: LinkedInstaller;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

const getOfficeId = (office?: Pick<Office, "_id"> & { id?: string } | null) =>
  String(office?._id || (office as any)?.id || "").trim();

type Supplier = {
  supplier_id: string;
  marketplace_profile_id?: string;
  organization_id?: string;
  name: string;
  email: string;
};

type SelectionState = {
  [productId: string]: string;
};
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));
const columnHelper = createColumnHelper<OfficeProduct>();

const RealtorOfficeInventory = () => {
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();
  const [officeProducts, setOfficeProducts] = useState<OfficeProduct[]>(
    []
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [linkedOfficeId, setLinkedOfficeId] = useState<string>("");
  const [linkedProductId, setLinkedProductId] = useState<string>("");
  const [linkedQuantity, setLinkedQuantity] = useState<number>(1);
  const [selectedSupplier, setSelectedSupplier] = useState<string>("");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredOfficeProducts, setFilteredOfficeProducts] = useState<Product[]>([]);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);

  const [addressMap, setAddressMap] = useState<Record<string, Address[]>>(
    {}
  );

  const [supplierSelections, setSupplierSelections] = useState<SelectionState>(
    {}
  );
  const [addressSelections, setAddressSelections] = useState<SelectionState>(
    {}
  );
  const [dateSelections, setDateSelections] = useState<SelectionState>({});
  const [methodSelections, setMethodSelections] = useState<SelectionState>({});
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [deleteTarget, setDeleteTarget] = useState<{ officeId: string; productId: string; name: string } | null>(null);

    const organizationId = getAuthCookie("organizationId") || "";
  const navigate = useNavigate();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.office_inventory
  );
  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (officeProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [officeProducts]);

  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
    ];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brokerage/organizations/${organizationId}/office-inventories/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const result = await response.json();
        console.log("Fetched data:", result);
        // setTempObject(data);
        // Ensure the response contains the expected structure
        if (result && Array.isArray(result?.data)) {
          setOfficeProducts(result?.data ?? []);
          setFilteredOfficeProducts(result?.data ?? []);
          setRowCount(result?.total ?? 0);
        } else {
          console.error("Unexpected API response structure:", result);
          setOfficeProducts([]);
          // setProducts(data.products);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token, globalFilter, pagination.pageIndex, pagination.pageSize, organizationId,isTeammember]);

  // Fetch suppliers from marketplace profiles (signage)
  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!token) return;
      try {
        const { results } = await fetchMarketplaceProfilesSelect2(token, {
          category: "signage",
          page: 1,
          limit: 100,
        });
        const mapped: Supplier[] = (results || [])
          .map((r: any) => ({
            // POST linked-installer expects supplier_membership_id
            supplier_id: String(r.membership_id || "").trim(),
            marketplace_profile_id: String(r.id || "").trim(),
            organization_id: String(r.organization_id || "").trim(),
            name: String(r.text || r.id || "").trim(),
            email: "",
          }))
          .filter((s: Supplier) => s.supplier_id && s.name);
        setSuppliers(mapped);
      } catch (err) {
        console.error("Error fetching suppliers (marketplace profiles):", err);
      }
    };
    fetchSuppliers();
  }, [token]);

  const normalizeSupplierVendorId = (raw?: string) => {
    if (!raw) return "";
    const direct = suppliers.find((s) => s.supplier_id === raw);
    if (direct) return direct.supplier_id;
    const byProfile = suppliers.find((s) => s.marketplace_profile_id === raw);
    if (byProfile) return byProfile.supplier_id;
    const byOrg = suppliers.find((s) => s.organization_id === raw);
    return byOrg?.supplier_id || raw;
  };

  // const handleSubmitLink = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   if (!selectedSupplier || !linkedProductId) {
  //     alert("Please select a supplier and enter a valid product ID.");
  //     return;
  //   }

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/office/${linkedOfficeId}/inventory/${linkedProductId}/link`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           linked_mode: "pending",
  //           linked_installer: {
  //             id: selectedSupplier,
  //             name: suppliers?.find((s) => s.supplier_id === selectedSupplier)
  //               ?.name,
  //             // state: "pending",
  //           },
  //           // reason:
  //           //   "Requesting to link this product with supplier for inventory management.",
  //         }),
  //       }
  //     );

  //     if (!response.ok) throw new Error("Failed to link product.");

  //     showSuccess("Product linked successfully!");
  //     handleCloseModal();
  //     navigate(0);
  //   } catch (error) {
  //     console.error("Error linking product:", error);
  //     showError("Failed to link product.");
  //   }
  // };

  const handleDelete = async (officeId: string, productId: string) => {
    setDeleting(productId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the product.");
      }

      showSuccess("Product deleted successfully!");
      // Update the state by removing the deleted license
      setOfficeProducts((prev) =>
        prev?.filter((product) => product.product_id !== productId)
      );
      setDeleting(null); // Clear the deleting state
      // navigate(`${baseRoute}/officeInventory`, {
      //   state: { successMessage: "Product added successfully!" },
      // });
      // navigate(0);
    } catch (err) {
      showError("Failed to delete the product.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null); // Clear the deleting state
    }
  };

  const unlinkProduct = async (officeId: string, productId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}/unlink`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // linked_installer: null
            linked_mode: "unlock_pending",
            locked_status: "locked",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to unlink the product.");
      }

      showSuccess("Product unlinked successfully!");
      navigate(0);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Error linking product."
      );
    }
  };

  const cancelLinking = async (officeId: string, productId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}/office-cancel-link`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason:
              "Requesting to cancel this product with supplier for inventory management.",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to unlink the product.");
      }

      showSuccess("Product unlinked successfully!");
      navigate(0);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Error linking product."
      );
    }
  };

  const handleSupplierChange = (productId: string, value: string) => {
    setSupplierSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleMethodChange = (
    productId: string,
    value: string,
    officeId: string
  ) => {
    setMethodSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));

    // if (value === "pickup_from_location" || value === "at_installation_site") {
    //   fetchUserAddresses(officeId);
    // }
  };

  const handleAddressChange = (productId: string, value: string) => {
    setAddressSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleDateChange = (productId: string, value: string) => {
    setDateSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleDeleteClick = (product: OfficeProduct) => {
    setDeleteTarget({
      officeId: getOfficeId(product.office),
      productId: product.product_id,
      name: product.product?.name  || "Product",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.officeId, deleteTarget.productId);
    setDeleteTarget(null);
  };

  const handleSubmitLink = async (productId: string, officeId: string) => {
    // Get the product from agentProducts
    const product = officeProducts?.find((p) => p.product_id === productId);

    // Get values from either state or fallback from product object
    const method =
      methodSelections[productId] ??
      product?.linked_installer_details?.product_location_status;

    const rawSupplierId =
      supplierSelections[productId] ??
      (product?.linked_installer_details as any)?.supplier_membership_id ??
      product?.linked_installer_details?.supplier_id;
    const supplierMembershipId = normalizeSupplierVendorId(rawSupplierId);

    const supplierName = suppliers?.find(
      (supplier) => supplier.supplier_id === supplierMembershipId
    )?.name;

    const dropOffDate = dateSelections[productId]
      ? new Date(dateSelections[productId]).toISOString()
      : product?.linked_installer_details?.drop_off_date;

    const addressId =
      addressSelections[productId] ??
      product?.linked_installer_details?.pickup_address?._id;

    const selectedAddress = addressMap[officeId]?.find(
      (address) => address._id === addressId
    );

    if (!officeId) {
      showError("Office ID is missing");
      return;
    }

    try {
      const payload: Record<string, unknown> = {
        supplier_membership_id: supplierMembershipId,
        supplier_name: supplierName,
        location_type: method,
        notes:
          method === "pickup_from_location"
            ? "Please pick up at my location"
            : "",
      };

      if (method === "scheduled_dropoff") {
        payload.drop_off_date = dropOffDate;
      } else {
        payload.address = selectedAddress;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to link product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product linked successfully!");

      // Send notifications for product link requested
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductLinkRequestedSignageContent } = await import("../../services/notificationContent");
        const { fetchUserData } = await import("utils/userDataFetcher");
        
        const product = officeProducts?.find((p) => p.product_id === productId);
        if (product && supplierMembershipId && token) {
          const productName = product?.product?.name || "Product";
          const requesterName = product?.office?.officeName || "Office User";
          
          // Send notification to supplier (signage)
          try {
            const supplierData = await fetchUserData(supplierMembershipId, token || "");
            if (supplierData) {
              const supplierLicenseType = supplierData.licenseType || "SIGNAGE_TEAM_MEMBER";
              const recipientType = supplierLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
              
              // Get effective signage ID (parent if team member, otherwise supplierId)
              const effectiveSignageId = supplierData.id || supplierMembershipId;
              
              // Fetch all signage team members and send notifications
              const signageTeamResp = await fetch(
                `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              
              if (signageTeamResp.ok) {
                const signageTeamData = await signageTeamResp.json();
                const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];
                
                for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  if (memberId && token) {
                    try {
                      const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                      const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                      const content = getProductLinkRequestedSignageContent({
                        agentOrTeamMemberName: requesterName,
                        productName: productName,
                        recipientType: memberRecipientType,
                      });
                      await createNotification(memberId, content, token, memberRecipientType);
                    } catch (memberErr) {
                      console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                    }
                  }
                }
              }
            }
          } catch (signageErr) {
            console.error("Failed to send product link requested notifications to signage:", signageErr);
          }
        }
      } catch (notifError) {
        console.error("Failed to send product link requested notifications:", notifError);
      }

      // Send email notifications for product link requested
      try {
        const { sendTriggerEmails } = await import("utils/emailService");
        const { fetchUserData } = await import("utils/userDataFetcher");
        const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";

        if (from && supplierMembershipId && product) {
          const productName = product?.product?.name || "Product";
          const requestDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
          const requestedBy = product?.office?.officeName || "Office";
          const reason = method === "pickup_from_location" ? "Please pick up at my location" : method === "scheduled_dropoff" ? `Scheduled drop-off on ${dropOffDate ? new Date(dropOffDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : "N/A"}` : "Product link requested";

          // Send to Supplier (Signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              const supplierEmail = supplierData?.email || supplierData?.firmDetails?.email;
              const supplierFirstName = supplierData?.firstName || supplierData?.firmDetails?.firstName || supplierEmail?.split("@")[0] || "Supplier";

              if (supplierEmail) {
                const supplierParams: Record<string, string> = {
                  firstName: supplierFirstName,
                  productName: productName,
                  requestedBy: requestedBy,
                  requestDate: requestDate,
                  reason: reason,
                };

                const supplierLicenseType = supplierData?.licenseType === "SIGNAGE_FIRM" ? "SIGNAGE_FIRM" : "SIGNAGE_TEAM_MEMBER";
                await sendTriggerEmails(
                  "Product link requested",
                  supplierLicenseType,
                  supplierParams,
                  supplierEmail,
                  from,
                  token || ""
                );
              }
            } catch (supplierErr) {
              console.error("Failed to send email to supplier:", supplierErr);
            }
          }

          // Send to Office Team Members (End Users) - Get all team members by office ID
          if (officeId) {
            try {
              const { fetchOfficeTeamMembers } = await import("utils/userDataFetcher");
              const teamMembers = await fetchOfficeTeamMembers(officeId, token || "");
              
              // Send email to each team member
              for (const teamMember of teamMembers) {
                if (teamMember?.email && teamMember.email.trim()) {
                  try {
                    const teamMemberFirstName = teamMember.firstName || teamMember.email.split("@")[0] || "Team Member";
                    const teamMemberLicenseType = teamMember.licenseType || "BROKERAGE_TEAM_MEMBER";

                    const teamMemberParams: Record<string, string> = {
                      firstName: teamMemberFirstName,
                      productName: productName,
                      supplierName: supplierName || "Supplier",
                      newStatus: "Link Requested",
                      updatedOn: requestDate,
                      supplierComment: reason,
                    };

                    const emailLicenseType = teamMemberLicenseType === "BROKERAGE_FIRM" ? "BROKERAGE_FIRM" : "BROKERAGE_TEAM_MEMBER";
                    await sendTriggerEmails(
                      "Product link requested",
                      emailLicenseType,
                      teamMemberParams,
                      teamMember.email.trim(),
                      from,
                      token || ""
                    );
                  } catch (memberErr) {
                    console.error(`Failed to send email to team member ${teamMember.email}:`, memberErr);
                  }
                }
              }
            } catch (officeErr) {
              console.error("Failed to fetch office team members or send emails:", officeErr);
            }
          }
        }
      } catch (emailError) {
        console.error("Failed to send product link requested emails:", emailError);
        // Don't block the operation if email fails
      }

      navigate(0);
    } catch (error) {
      console.error("Error linking product:", error);
    }
  };

  const handleUnLink = async (productId: string, officeId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/request-unlink`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product: {
              linked_mode: "unlock_pending",
              locked_status: "locked",
            },
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to Unlink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product Unlink requested successfully!");

      // Send notifications for product unlink requested
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductUnlinkedSignageContent } = await import("../../services/notificationContent");
        const { fetchUserData } = await import("utils/userDataFetcher");
        
        const product = officeProducts?.find((p) => p.product_id === productId);
        if (product && token) {
          const productName = product?.product?.name || "Product";
          const requesterName = product?.office?.officeName || "Office User";
          const rawSupplierId =
            (product?.linked_installer_details as any)?.supplier_membership_id ??
            product?.linked_installer_details?.supplier_id;
          const supplierMembershipId = normalizeSupplierVendorId(rawSupplierId);
          
          // Send notification to supplier (signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              if (supplierData) {
                const effectiveSignageId = supplierData.id || supplierMembershipId;
                
                // Fetch all signage team members and send notifications
                const signageTeamResp = await fetch(
                  `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                  {
                    method: "GET",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                
                if (signageTeamResp.ok) {
                  const signageTeamData = await signageTeamResp.json();
                  const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];
                  
                  for (const member of signageTeamMembers) {
                    const memberId = member.id || member._id;
                    if (memberId && token) {
                      try {
                        const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                        const memberRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                        const content = getProductUnlinkedSignageContent({
                          agentOrTeamMemberName: requesterName,
                          productName: productName,
                          recipientType: memberRecipientType,
                        });
                        await createNotification(memberId, content, token, memberRecipientType);
                      } catch (memberErr) {
                        console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                      }
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.error("Failed to send product unlink requested notifications to signage:", signageErr);
            }
          }
        }
      } catch (notifError) {
        console.error("Failed to send product unlink requested notifications:", notifError);
      }

      // Send email notifications for product unlinked
      try {
        const { sendTriggerEmails } = await import("utils/emailService");
        const { fetchUserData } = await import("utils/userDataFetcher");
        const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";

        const product = officeProducts?.find((p) => p.product_id === productId);
        if (from && product) {
          const productName = product?.product?.name || "Product";
          const unlinkedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
          const supplierMembershipId = normalizeSupplierVendorId(
            (product?.linked_installer_details as any)?.supplier_membership_id ??
              product?.linked_installer_details?.supplier_id
          );
          const supplierName = suppliers?.find((s) => s.supplier_id === supplierMembershipId)?.name || "Supplier";
          const reason = "Product unlink requested";
          const brokerageFirmName = product?.office?.officeName || "Brokerage Firm";

          // Send to Supplier (Signage)
          if (supplierMembershipId) {
            try {
              const supplierData = await fetchUserData(supplierMembershipId, token || "");
              const supplierEmail = supplierData?.email || supplierData?.firmDetails?.email;
              const supplierFirstName = supplierData?.firstName || supplierData?.firmDetails?.firstName || supplierEmail?.split("@")[0] || "Supplier";

              if (supplierEmail) {
                const supplierParams: Record<string, string> = {
                  firstName: supplierFirstName,
                  productName: productName,
                  supplierName: supplierName,
                  brokerageFirmName: brokerageFirmName,
                  unlinkedOn: unlinkedOn,
                  reason: reason,
                };

                const supplierLicenseType = supplierData?.licenseType === "SIGNAGE_FIRM" ? "SIGNAGE_FIRM" : "SIGNAGE_TEAM_MEMBER";
                await sendTriggerEmails(
                  "Product Unlinked",
                  supplierLicenseType,
                  supplierParams,
                  supplierEmail,
                  from,
                  token || ""
                );
              }
            } catch (supplierErr) {
              console.error("Failed to send email to supplier:", supplierErr);
            }
          }

          // Send to Office Team Members (End Users) - Get all team members by office ID
          if (officeId) {
            try {
              const { fetchOfficeTeamMembers } = await import("utils/userDataFetcher");
              const teamMembers = await fetchOfficeTeamMembers(officeId, token || "");
              
              // Send email to each team member
              for (const teamMember of teamMembers) {
                if (teamMember?.email && teamMember.email.trim()) {
                  try {
                    const teamMemberFirstName = teamMember.firstName || teamMember.email.split("@")[0] || "Team Member";
                    const teamMemberLicenseType = teamMember.licenseType || "BROKERAGE_TEAM_MEMBER";

                    const teamMemberParams: Record<string, string> = {
                      firstName: teamMemberFirstName,
                      productName: productName,
                      supplierName: supplierName,
                      brokerageFirmName: brokerageFirmName,
                      unlinkedOn: unlinkedOn,
                      reason: reason,
                    };

                    const emailLicenseType = teamMemberLicenseType === "BROKERAGE_FIRM" ? "BROKERAGE_FIRM" : "BROKERAGE_TEAM_MEMBER";
                    await sendTriggerEmails(
                      "Product Unlinked",
                      emailLicenseType,
                      teamMemberParams,
                      teamMember.email.trim(),
                      from,
                      token || ""
                    );
                  } catch (memberErr) {
                    console.error(`Failed to send email to team member ${teamMember.email}:`, memberErr);
                  }
                }
              }
            } catch (officeErr) {
              console.error("Failed to fetch office team members or send emails:", officeErr);
            }
          }
        }
      } catch (emailError) {
        console.error("Failed to send product unlinked emails:", emailError);
        // Don't block the operation if email fails
      }

      navigate(0);
    } catch (error) {
      console.error("Error canceling product link:", error);
    }
  };

  const handleCancelLink = async (productId: string, officeId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/cancel`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product: {
              linked_mode: "unlock_pending",
              locked_status: "locked",
            },
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to CancelLink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product Unlink Cancel!");
      navigate(0);
    } catch (error) {
      console.error("Error canceling product link:", error);
    }
  };

  useEffect(() => {
    const fetchAllOfficeAddresses = async () => {
      const uniqueOfficeIds = Array.from(
        new Set(
          (officeProducts || [])
            .map((p) => getOfficeId(p.office))
            .filter(Boolean)
        )
      );

      for (const officeId of uniqueOfficeIds) {
        if (addressMap[officeId]) continue;
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/offices/${officeId}/addresses`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          if (!response.ok || data?.error) {
            throw new Error(data?.error || data?.message || `Failed to fetch addresses for ${officeId}`);
          }
          const list: Address[] = (Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.addresses)
                ? data.addresses
                : Array.isArray(data?.results)
                  ? data.results
                  : Array.isArray(data?.officeAddress)
                    ? data.officeAddress
                    : []
          ).map((row: any) => ({
            ...row,
            _id: String(row?._id ?? row?.id ?? ""),
            label: String(row?.label ?? row?.address_details?.label ?? "Address"),
          })).filter((row: Address) => row._id);

          setAddressMap((prev) => ({
            ...prev,
            [officeId]: list,
          }));
        } catch (err) {
          console.error(
            `Failed to fetch addresses for office ${officeId}`,
            err
          );
        }
      }
    };

    if (officeProducts?.length > 0) {
      fetchAllOfficeAddresses();
    }
  }, [officeProducts, token]);



  const columns = useMemo(() => [
    // columnHelper.display({
    //   id: "serial",
    //   header: () => <div className="th-text S-No">S.No.</div>,
    //   cell: (info) => (
    //     <div className="text-sm S-No">
    //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
    //     </div>
    //   ),
    // }),
    columnHelper.display({
      id: "image",
      header: () => <div className="th-text S-No">Image</div>,
      cell: (info) => {
        const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
        let url = "";

        if (raw) {
          if (ABS_URL.test(raw)) {
            // already a full URL
            url = raw;
          } else if (isHexId(raw)) {
            // hex id -> resolve via presigned map / helper
            const map: Record<string, string> = (imageUrlMap as any) || {};
            url = map[raw] || getImageSrc(raw, map) || "";
          } else {
            // legacy relative path
            const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
            const path = raw.replace(/^\/+/, "");
            url = `${base}/${encodeURI(path)}`;
          }
        }

        const src = url || imageCustomSquare;

        return (
          <div className="text-sm S-No">
            <img
              src={src}
              alt="Product"
              style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
              }}
            />
          </div>
        );


      },
      enableGlobalFilter: false,
    }),
    // columnHelper.accessor(
    //   (row: OfficeProduct) => `${row?.office?.brokerage_firm}`,
    //   {
    //     id: "Brokerage Name",
    //     header: () => <div className="th-text">Brokerage Name</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //     cell: info => <span className="table-cells d-flex justify-content-center">{"-"}</span>,
    //   }),
    columnHelper.accessor(
      (row: OfficeProduct) => `${row?.office?.officeName}`,
      {
        id: "office",
        header: () => <div className="th-text">Office</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row?.product?.name,
      {
        id: "name",
        header: () => <div className="th-text">Product Name</div>,
        cell: (info) => <div className="text-sm text-ellipsis wrap-anywhere">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row.product.sku || "-",
      {
        id: "sku",
        header: () => <div className="th-text">SKU</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row.product.category || "-",
      {
        id: "category",
        header: () => <div className="th-text">Category</div>,
        cell: (info) => <div className="text-sm text-ellipsis">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
      {
        id: "qty",
        header: () => <div className="th-text">Qty</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row.product.item_condition || "-",
      {
        id: "condition",
        header: () => <div className="th-text">Condition</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.display({
      id: "locationStatusActions",
      header: () => (
        <div className="th-text">Handover Method</div>
      ),
      cell: ({ row }) => {
        const officeproduct = row.original;
        const isLocked = officeproduct?.product?.locked_status === "locked";
        const isDisabled =
          isLocked || officeproduct?.linked_installer_details?.current_status === "in_transit";

        // ✅ Locked → Only show Supplier Select (disabled)
        if (isLocked) {
          return (
            <div className="text-sm">
              <select
                className="form-control mb-2 supplier-select-r form-select po-select"
                value={
                  supplierSelections[officeproduct.product_id] ??
                  normalizeSupplierVendorId(
                    (officeproduct?.linked_installer_details as any)?.supplier_membership_id ??
                      officeproduct?.linked_installer_details?.supplier_id
                  ) ??
                  ""
                }
                onChange={(e) =>
                  handleSupplierChange(officeproduct.product_id, e.target.value)
                }
                disabled={true}
              >
                <option value="">- Select Supplier -</option>
                {suppliers?.map((supplier) => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        // ✅ Otherwise → Full UI
        return (
          <div className="text-sm">
            {/* Supplier Select */}
            <select
              className="form-control mb-2 supplier-select-r form-select po-select"
              value={
                supplierSelections[officeproduct.product_id] ??
                normalizeSupplierVendorId(
                  (officeproduct?.linked_installer_details as any)?.supplier_membership_id ??
                    officeproduct?.linked_installer_details?.supplier_id
                ) ??
                ""
              }
              onChange={(e) =>
                handleSupplierChange(officeproduct.product_id, e.target.value)
              }
              disabled={isDisabled}
            >
              <option value="">- Select Supplier -</option>
              {suppliers?.map((supplier) => (
                <option key={supplier.supplier_id} value={supplier.supplier_id}>
                  {supplier.name}
                </option>
              ))}
            </select>

            {/* Method Select */}
            <select
              className="form-control mb-2 supplier-select-r form-select po-select"
              value={
                methodSelections[officeproduct.product_id] ??
                officeproduct?.product?.product_location_status ??
                ""
              }
              onChange={(e) =>
                handleMethodChange(
                  officeproduct.product_id,
                  e.target.value,
                  getOfficeId(officeproduct.office)
                )
              }
              disabled={isDisabled}
            >
              <option value="">- Select Method -</option>
              <option value="scheduled_dropoff">Scheduled for Drop-off</option>
              <option value="pickup_from_location">Pickup from my location</option>
            </select>

            {/* Conditional date or address select */}
            {(methodSelections[officeproduct.product_id] ??
              officeproduct?.product?.product_location_status) ===
              "scheduled_dropoff" ? (
              <input
                type="date"
                className="form-control mb-2 supplier-select-r form-select po-select"
                min={new Date().toISOString().split("T")[0]}
                value={
                  dateSelections[officeproduct.product_id] ??
                  (officeproduct?.linked_installer_details?.drop_off_date
                    ? new Date(
                      officeproduct.linked_installer_details.drop_off_date
                    )
                      .toISOString()
                      .split("T")[0]
                    : "")
                }
                onChange={(e) =>
                  handleDateChange(officeproduct.product_id, e.target.value)
                }
                disabled={isDisabled}
              />
            ) : (
              <select
                className="form-control mb-2 supplier-select-r form-select po-select"
                value={
                  addressSelections[officeproduct.product_id] ??
                  officeproduct?.linked_installer_details?.pickup_address?._id ??
                  ""
                }
                onChange={(e) =>
                  handleAddressChange(officeproduct.product_id, e.target.value)
                }
                disabled={isDisabled}
              >
                <option value="">- Select Location -</option>
                {(addressMap[getOfficeId(officeproduct.office)] || []).map((address) => (
                  <option key={address._id} value={address._id}>
                    {address.label}
                  </option>
                ))}
              </select>
            )}

            {/* Confirm/Update Button */}
            <div className="text-center">
              <button
                className="btn btn-success confirm-btn"
                onClick={() =>
                  handleSubmitLink(officeproduct.product_id, getOfficeId(officeproduct.office))
                }
                disabled={isDisabled}
              >
                {officeproduct?.linked_installer_details ? "Update" : "Confirm"}
              </button>
            </div>
          </div>
        );
      }
    }),
    // columnHelper.display({
    //   id: "locationStatusActions",
    //   header: () => <div className="th-text">Handover Method</div>,
    //   cell: ({ row }) => {
    //     const officeproduct = row.original;
    //     console.log("officeproduct", officeproduct);
    //     return (
    //       <div className="text-sm">
    //         {/* Supplier Select */}
    //         <select
    //           className="form-control mb-2 supplier-select-r form-select po-select"
    //           value={supplierSelections[officeproduct.product_id] ?? officeproduct?.linked_installer_details?.supplier_id ?? ""}
    //           onChange={(e) => handleSupplierChange(officeproduct.product_id, e.target.value)}
    //           disabled={officeproduct?.product?.locked_status === "locked" || officeproduct?.linked_installer_details?.current_status === "in_transit"}
    //         >
    //           <option value="">- Select Supplier -</option>
    //           {suppliers?.map((supplier) => (
    //             <option key={supplier.supplier_id} value={supplier.supplier_id}>
    //               {supplier.name}
    //             </option>
    //           ))}
    //         </select>
    //         {/* Method Select */}
    //         <select
    //           className="form-control mb-2 supplier-select-r form-select po-select"
    //           value={methodSelections[officeproduct.product_id] ?? officeproduct?.product?.product_location_status ?? ""
    //           }
    //           onChange={(e) =>
    //             handleMethodChange(
    //               officeproduct.product_id,
    //               e.target.value,
    //               officeproduct.office._id
    //             )
    //           }
    //           disabled={officeproduct?.product?.locked_status === "locked" || officeproduct?.product?.current_status === "in_transit"}
    //         >
    //           <option value="">- Select Method -</option>
    //           <option value="scheduled_dropoff">Scheduled for Drop-off</option>
    //           <option value="pickup_from_location">Pickup from my location</option>
    //           <option value="at_installation_site">At installation site</option>
    //         </select>
    //         {/* Conditional date or address select */}
    //         {(methodSelections[officeproduct.product_id] ??
    //           officeproduct?.product?.product_location_status) === "scheduled_dropoff" ? (
    //           <input
    //             disabled={officeproduct?.product?.locked_status === "locked" || officeproduct?.product?.current_status === "in_transit"}
    //             type="date"
    //             className="form-control mb-2 supplier-select-r form-select po-select"
    //             value={
    //               dateSelections[officeproduct.product_id] ??
    //               (officeproduct?.linked_installer_details?.drop_off_date
    //                 ? new Date(officeproduct.linked_installer_details.drop_off_date).toISOString().split("T")[0]
    //                 : "")
    //             }
    //             onChange={(e) => handleDateChange(officeproduct.product_id, e.target.value)}
    //           />
    //         ) : (
    //           <select
    //             className="form-control mb-2 supplier-select-r form-select po-select"
    //             value={
    //               addressSelections[officeproduct.product_id] ??
    //               officeproduct?.linked_installer_details?.pickup_address?._id ?? ""
    //             }
    //             onChange={(e) => handleAddressChange(officeproduct.product_id, e.target.value)}
    //             disabled={officeproduct?.product?.locked_status === "locked" || officeproduct?.product?.current_status === "in_transit"}
    //           >
    //             <option value="">- Select Location -</option>
    //             {(addressMap[officeproduct.office._id] || []).map((address) => (
    //               <option key={address._id} value={address._id}>
    //                 {address.label}
    //               </option>
    //             ))}
    //           </select>
    //         )}
    //         <button
    //           className="btn btn-success"
    //           onClick={() => handleSubmitLink(officeproduct.product_id, officeproduct.office._id)}
    //           disabled={officeproduct?.product?.locked_status === "locked" || officeproduct?.product?.current_status === "in_transit"}
    //         >
    //           {officeproduct?.linked_installer_details ? "Update" : "Confirm"}
    //         </button>
    //       </div>
    //     );
    //   }
    // }),
    // columnHelper.accessor(
    //   (row: OfficeProduct) =>
    //     row?.product?.current_status &&
    //       row.product.current_status !== "none"
    //       ? row.product.current_status.replace(/_/g, " ")
    //       : "-",
    //   {
    //     id: "currentStatus",
    //     header: () => <div className="th-text">Product location status</div>,
    //     cell: (info) => <div className="text-sm">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }),
    columnHelper.accessor(
      (row: OfficeProduct) =>
        row.product.current_status?.replace(/_/g, " ") || "-",
      {
        id: "current_status",
        header: () => (
          <div className="text-center table-headers">
            Product location status
          </div>
        ),
        cell: info => (
          <div className="text-center table-cells">
            <StatusBadge status={info.getValue()} />
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),
    columnHelper.accessor(
      (row: OfficeProduct) =>
        row?.product?.linked_mode === "unlock_pending"
          ? "Unlink Pending"
          : row?.product?.locked_status === "unlocked"
            ? ""
            : "Linked",
      {
        id: "linkedStatus",
        header: () => <div className="th-text">Linked Status</div>,
        cell: (info) => <div className="text-sm linked-status">{info.getValue() || "-"}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: OfficeProduct) => row.linked_installer_details?.supplier_name || "-",
      {
        id: "supplier",
        header: () => <div className="th-text">Linked Supplier</div>,
        cell: (info) => <div className="text-sm ">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const productInfo = info.row.original;
        const officeproduct = info.row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            {canView && (
              <ViewIconButton
                to={`${baseRoute}/office/${getOfficeId(productInfo.office)}/viewOfficeProduct/${productInfo.product_id}`}
                className="btn action-btn fs-2 text-center"
                title="View"
              />
            )}

            {
              canEdit &&
              !(officeproduct?.product?.locked_status === "locked" || officeproduct?.linked_installer_details?.current_status === "in_transit")
              &&
              <EditIconButton
                to={`${baseRoute}/officeProduct/${getOfficeId(productInfo.office)}/editOfficeProduct/${productInfo.product_id}`}
                className="icon-square"
                title="Edit"
              />
            }
            {productInfo?.product?.locked_status === "unlocked" ? (
              canDelete && (
                <DeleteIconButton
                  title="Delete"
                  ariaLabel="Delete"
                  onClick={() => handleDeleteClick(productInfo)}
                  disabled={deleting === productInfo.product_id}
                />
              )
            ) : (
              <UnlinkIconButton
                onClick={() => handleUnLink(productInfo?.product_id, getOfficeId(productInfo.office))}
                className="btn btn-danger btn-sm"
                title="Unlink"
                aria-label="Unlink"
                disabled={productInfo?.product?.linked_mode === "unlock_pending"}

              />
            )}
            {(productInfo?.product?.current_status === "pending_dropoff" ||
              productInfo?.product?.current_status === "awaiting_response") && (
                <CancelIconButton
                  className="btn btn-sm btn-danger"
                  onClick={() => handleCancelLink(productInfo?.product_id, getOfficeId(productInfo.office))}
                />
              )}
          </div>
        );
      },
    }),
  ], [
    deleting,
    suppliers,
    supplierSelections,
    methodSelections,
    addressSelections,
    dateSelections,
    handleSupplierChange,
    handleMethodChange,
    handleAddressChange,
    handleDateChange,
    handleSubmitLink,
    imageUrlMap,
    canView,
    canEdit,
    canDelete,
  ]);




  // useEffect(() => {
  //     const rows: ExportRow[] = filteredOfficeProducts.map(product => ({
  //       "Office Name": product?.office?.officeName || '',
  //       "Product Name": product?.product?.name || '',
  //       "SKU": product?.product?.sku || '',
  //       "Quantity": product?.product?.variants?.[0]?.inventory?.[0]?.available || 0,
  //       "Brand": product?.product?.brand || '',
  //       "Category": product?.product?.category || '',
  //       "Item Condition": product?.product?.item_condition || '',
  //       "Status": product?.product?.status ? 'Active' : 'Inactive',
  //     }));

  //     setExportRows(rows);
  //   }, [filteredOfficeProducts]);

  const table = useReactTable({
    data: officeProducts,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="realtor">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/addOfficeProduct`} className="btn btn-primary  table-addbutton">
              + Add Office Product
            </Link>
          )}

        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div>
            <h4 className="po-title mb-1">Office Inventory</h4>
            <div className="po-subtitle">Manage and link your office products with suppliers</div>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name or SKU"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
            <ExportAsExcel
              data={exportRows}
              headers={[
                "Office Name",
                "Product Name",
                "SKU",
                "Quantity",
                "Brand",
                "Category",
                "Item Condition",
                "Status"
              ]}
              fileName="OfficeInventory"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i
              className="bi bi-box-seam me-2"
              aria-hidden="true"
              style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
            />
            <h5 className="mb-0">Products</h5>
          </div>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* {products.length} orders */}
          </span>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Product"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />

      <style>{`
        /* Hide dropdown arrow for disabled select fields */
        select.form-select:disabled,
        select.po-select:disabled,
        select.supplier-select-r:disabled {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: none !important;
          padding-right: 12px;
        }
      `}</style>
    </div>
  );
};

export default RealtorOfficeInventory;





// const columns = useMemo(() => {
//   return [
//     columnHelper.display({
//       id: "serial",
//       header: "S.No.",
//       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
//     }),
//     columnHelper.display({
//       id: "image",
//       header: "Image",
//       cell: (info) => {
//         const url = `${process.env.REACT_APP_BASE_URL}${info.row.original?.product?.image?.url}`;
//         return info.row.original?.product?.image?.url ? (
//           <img src={url} alt="Product" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }} />
//         ) : (
//           <span>No Image</span>
//         );
//       },
//       enableGlobalFilter: false,
//     }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row?.product?.name,
//       {
//         header: "Product Name",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row.product.sku || "-",
//       {
//         header: "SKU",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row.product.category || "-",
//       {
//         header: "Category",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
//       {
//         header: "Qty",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row.product.item_condition || "-",
//       {
//         header: "Condition",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) => row.linked_installer_details?.supplier_name || "-",
//       {
//         header: "Linked Supplier",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.display({
//       id: "locationStatusActions",
//       header: "Product Location Status",
//       cell: ({ row }) => {
//         const officeproduct = row.original;

//         return (
//           <>
//             {/* Supplier Select */}
//             <select
//               className="form-control mb-2 supplier-select-r form-select po-select"
//               value={supplierSelections[officeproduct.product_id] ?? officeproduct?.linked_installer_details?.supplier_id ?? ""}
//               onChange={(e) => handleSupplierChange(officeproduct.product_id, e.target.value)
//               }
//               disabled={
//                 officeproduct?.product?.locked_status ===
//                 "locked" ||
//                 officeproduct?.linked_installer_details
//                   ?.current_status === "in_transit"
//               }
//             >
//               <option value="">- Select Supplier -</option>
//               {suppliers?.map((supplier) => (
//                 <option
//                   key={supplier.supplier_id}
//                   value={supplier.supplier_id}
//                 >
//                   {supplier.name}
//                 </option>
//               ))}
//             </select>

//             {/* Method Select */}
//             <select
//               className="form-control mb-2 supplier-select-r form-select po-select"
//               value={
//                 methodSelections[
//                 officeproduct.product_id
//                 ] ??
//                 officeproduct?.linked_installer_details
//                   ?.product_location_status ??
//                 ""
//               }
//               onChange={(e) =>
//                 handleMethodChange(
//                   officeproduct.product_id,
//                   e.target.value,
//                   officeproduct.office._id
//                 )
//               }
//               disabled={
//                 officeproduct?.product?.locked_status ===
//                 "locked" ||
//                 officeproduct?.linked_installer_details
//                   ?.current_status === "in_transit"
//               }
//             >
//               <option value="">- Select Method -</option>
//               <option value="scheduled_dropoff">
//                 Scheduled for Drop-off
//               </option>
//               <option value="pickup_from_location">
//                 Pickup from my location
//               </option>
//               <option value="at_installation_site">
//                 At installation site
//               </option>
//             </select>

//             {/* Conditional date or address select */}
//             {(methodSelections[officeproduct.product_id] ??
//               officeproduct?.linked_installer_details
//                 ?.product_location_status) ===
//               "scheduled_dropoff" ? (
//               <input
//                 disabled={
//                   officeproduct?.product?.locked_status ===
//                   "locked"
//                 }
//                 type="date"
//                 className="form-control mb-2 supplier-select-r form-select po-select"
//                 value={
//                   dateSelections[
//                   officeproduct.product_id
//                   ] ??
//                   (officeproduct?.linked_installer_details
//                     ?.drop_off_date
//                     ? new Date(
//                       officeproduct.linked_installer_details.drop_off_date
//                     )
//                       .toISOString()
//                       .split("T")[0]
//                     : "")
//                 }
//                 onChange={(e) =>
//                   handleDateChange(
//                     officeproduct.product_id,
//                     e.target.value
//                   )
//                 }
//               />
//             ) : (
//               <select
//                 className="form-control mb-2 supplier-select-r form-select po-select"
//                 value={
//                   addressSelections[
//                   officeproduct.product_id
//                   ] ??
//                   officeproduct?.linked_installer_details
//                     ?.pickup_address?._id ??
//                   ""
//                 }
//                 onChange={(e) =>
//                   handleAddressChange(
//                     officeproduct.product_id,
//                     e.target.value
//                   )
//                 }
//                 disabled={
//                   officeproduct?.product?.locked_status ===
//                   "locked" ||
//                   officeproduct?.linked_installer_details
//                     ?.current_status === "in_transit"
//                 }
//               >
//                 <option value="">
//                   - Select Location -
//                 </option>
//                 {(
//                   addressMap[officeproduct.office._id] || []
//                 ).map((address) => (
//                   <option
//                     key={address._id}
//                     value={address._id}
//                   >
//                     {address.label}
//                   </option>
//                 ))}
//               </select>
//             )}
//             <button
//               className="btn btn-success w-100"
//               onClick={() =>
//                 handleSubmitLink(
//                   officeproduct.product_id,
//                   officeproduct.office._id
//                 )
//               }
//               disabled={
//                 officeproduct?.product?.locked_status ===
//                 "locked" ||
//                 officeproduct?.linked_installer_details
//                   ?.current_status === "in_transit"
//               }
//             >
//               {officeproduct?.linked_installer_details
//                 ? "Update"
//                 : "Confirm"}
//             </button>
//           </>
//         );
//       }
//     }),
//     // columnHelper.display({
//     //   id: "supplierActions",
//     //   header: "Supplier Actions",
//     //   cell: ({ row }) => {
//     //     const officeProduct = row.original;

//     //     return (
//     //       <>
//     //         {officeProduct?.product?.linked_mode === "unlock_pending" && (
//     //           <>
//     //             <p className="text-danger mb-0 fw-bold">Unlink Requested</p>
//     //             <button
//     //               className="btn btn-danger btn-sm mb-2"
//     //               onClick={() =>
//     //                 handleUnlink(
//     //                   officeProduct?.product_id,
//     //                   officeProduct?.office._id
//     //                 )
//     //               }
//     //             >
//     //               Approval Unlink
//     //             </button>
//     //             <textarea
//     //               className="form-control"
//     //               placeholder="Add Reason..."
//     //               value={noteSelections[officeProduct.product_id] ?? ""}
//     //               onChange={(e) =>
//     //                 handleNoteChange(
//     //                   officeProduct.product_id,
//     //                   e.target.value
//     //                 )
//     //               }
//     //             />
//     //           </>
//     //         )}
//     //       </>
//     //     );
//     //   },
//     //   enableGlobalFilter: false, // disable if not filterable
//     // }),

//     columnHelper.accessor(
//       (row: OfficeProduct) =>
//         row?.product?.current_status &&
//           row.product.current_status !== "none" ? row.product.current_status.replace(
//             /_/g,
//             " "
//           )
//           : "-"
//       ,
//       {
//         header: "Current Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: OfficeProduct) =>
//         row?.product?.linked_mode ===
//           "unlock_pending"
//           ? "Unlink Pending"
//           : row?.product?.locked_status ===
//             "unlocked"
//             ? ""
//             : "Linked"
//       ,
//       {
//         header: "Linked Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.display({
//       id: "actions",
//       header: "Actions",
//       cell: (info) => {
//         const productInfo = info.row.original;
//         const officeproduct = info.row.original;
//         return (
//           <div className="d-flex gap-2">
//             <Link
//               to={`${baseRoute}/office/${productInfo.office._id}/viewOfficeProduct/${productInfo.product_id}`}
//               className="view me-3"
//             >
//               <i className="ri-eye-fill fs-20"></i>
//             </Link>
//             {
//               !(officeproduct?.product?.locked_status ===
//                 "locked" ||
//                 officeproduct?.linked_installer_details
//                   ?.current_status === "in_transit")
//               &&
//               <Link
//                 to={`${baseRoute}/officeProduct/${productInfo.office._id}/editOfficeProduct/${productInfo.product_id}`}
//                 className="edit me-3"
//               >
//                 <i className="ri-pencil-fill fs-20"></i>
//               </Link>
//             }
//             {productInfo?.product?.locked_status ===
//               "unlocked" ? (
//               <button
//                 onClick={() =>
//                   handleDelete(
//                     productInfo.office._id,
//                     productInfo.product_id
//                   )
//                 }
//                 className="del border-0 p-0"
//               >
//                 <i className="ri-delete-bin-4-fill fs-18"></i>
//               </button>
//             ) : (
//               <button
//                 onClick={() =>
//                   handleUnLink(
//                     productInfo?.product_id,
//                     productInfo.office._id
//                   )
//                 }
//                 className="btn btn-danger btn-sm"
//                 disabled={
//                   productInfo?.product?.linked_mode ===
//                   "unlock_pending"
//                 }
//               >
//                 Unlink
//               </button>
//             )}
//             {(productInfo?.product?.current_status ===
//               "pending_dropoff" ||
//               productInfo?.product?.current_status ===
//               "awaiting_response") && (
//                 <button
//                   className="btn btn-sm btn-danger"
//                   onClick={() =>
//                     handleCancelLink(
//                       productInfo?.product_id,
//                       productInfo.office._id
//                     )
//                   }
//                 >
//                   Cancel
//                 </button>
//               )}
//           </div>
//         );
//       },
//     }),
//   ];
// }, [
//   deleting,
//   suppliers,
//   supplierSelections,
//   methodSelections,
//   addressSelections,
//   dateSelections,
//   handleSupplierChange,
//   handleMethodChange,
//   handleAddressChange,
//   handleDateChange,
//   handleSubmitLink,
// ]);